import defaultBranding from "@/branding/defaultBranding";

const isBrowser = typeof window !== "undefined";

const deepMerge = (base, overrides) => {
  if (!overrides || typeof overrides !== "object") return { ...base };
  const result = Array.isArray(base) ? [...base] : { ...base };

  Object.keys(overrides).forEach((key) => {
    const baseValue = base?.[key];
    const overrideValue = overrides[key];

    if (
      baseValue &&
      typeof baseValue === "object" &&
      !Array.isArray(baseValue) &&
      typeof overrideValue === "object" &&
      !Array.isArray(overrideValue)
    ) {
      result[key] = deepMerge(baseValue, overrideValue);
    } else if (overrideValue !== undefined) {
      result[key] = overrideValue;
    }
  });

  return result;
};

export const resolveBranding = (overrides = {}) =>
  deepMerge(defaultBranding, overrides || {});

export const getCssVar = (name, fallback) => {
  if (!isBrowser) return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
};

export const getBrandFontStack = () =>
  getCssVar("--font-sans", defaultBranding.fonts.sans);

export const getBrandColor = (token, fallback) => {
  const normalized = token.startsWith("--") ? token : `--color-${token}`;
  return getCssVar(normalized, fallback);
};

const setCssVar = (name, value) => {
  if (!isBrowser || !name || value === undefined || value === null) return;
  document.documentElement.style.setProperty(name, value);
};

const applyColorScale = (scaleName, values = {}) => {
  Object.entries(values).forEach(([key, value]) => {
    if (!value) return;
    setCssVar(`--color-${scaleName}-${key}`, value);
  });
};

const setFavicon = (favicon) => {
  if (!isBrowser || !favicon) return;
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = favicon;
};

export const applyBrandingToDocument = (branding) => {
  if (!isBrowser || !branding) return;

  const { colors, fonts, surfaces, appTitle, assets, text, shadows } = branding;

  if (fonts?.sans) {
    setCssVar("--font-sans", fonts.sans);
  }
  if (fonts?.heading) {
    setCssVar("--font-sans-heading", fonts.heading);
  }

  if (colors?.primary) applyColorScale("primary", colors.primary);
  if (colors?.secondary) applyColorScale("secondary", colors.secondary);
  if (colors?.gray) applyColorScale("gray", colors.gray);
  if (colors?.slate) applyColorScale("slate", colors.slate);

  if (colors?.primary) {
    const primaryDark = colors.primary[700] || colors.primary[600];
    const primaryLight = colors.primary[400] || colors.primary[500];
    if (primaryDark) setCssVar("--e-global-color-90c2a1a", primaryDark);
    if (primaryLight) setCssVar("--e-global-color-27c3296", primaryLight);
  }

  if (colors?.primary) {
    const primaryDark = colors.primary[700] || colors.primary[600];
    const primaryLight = colors.primary[400] || colors.primary[500];
    if (branding?.buttons?.primaryGradient && primaryDark && primaryLight) {
      setCssVar(
        "--btn-primary-bg",
        `linear-gradient(90deg, ${primaryDark} 0%, ${primaryLight} 100%)`
      );
    } else {
      setCssVar("--btn-primary-bg", "none");
    }
  }

  if (surfaces?.base) setCssVar("--surface-base", surfaces.base);
  if (surfaces?.card) setCssVar("--surface-card", surfaces.card);
  if (surfaces?.muted) setCssVar("--surface-muted", surfaces.muted);
  if (surfaces?.borderSubtle)
    setCssVar("--border-subtle", surfaces.borderSubtle);
  if (surfaces?.contrast) setCssVar("--surface-contrast", surfaces.contrast);

  if (text?.primary) setCssVar("--text-primary", text.primary);
  if (text?.secondary) setCssVar("--text-secondary", text.secondary);
  if (text?.muted) setCssVar("--text-muted", text.muted);
  if (text?.onPrimary) setCssVar("--text-on-primary", text.onPrimary);
  if (text?.heading) setCssVar("--text-heading", text.heading);
  if (text?.onContrast) setCssVar("--text-on-contrast", text.onContrast);

  if (shadows?.card) setCssVar("--shadow-card", shadows.card);
  if (shadows?.cardInset) setCssVar("--shadow-card-inset", shadows.cardInset);
  if (shadows?.glow) setCssVar("--shadow-glow", shadows.glow);
  if (shadows?.button) setCssVar("--shadow-button", shadows.button);

  if (appTitle) {
    document.title = appTitle;
  }

  if (assets?.favicon) {
    setFavicon(assets.favicon);
  }
};

export const withBrandDefaults = (branding, defaults) => {
  return {
    ...defaults,
    ...branding,
  };
};
